/**
 * Vorlagen: eigene Vorlagen und die Galerie fertiger Gestaltungen.
 *
 * Ein leeres Blatt ist die größte Hürde. Deshalb steht die Galerie oben:
 * sechs fertige Newsletter mit echten Bildern der Kanzlei, die nur noch
 * Texte brauchen. Wer lieber frei baut, nimmt das leere Grundgerüst.
 *
 * Der KI-Entwurf sitzt bewusst daneben und nicht davor — er ist eine
 * Einstiegshilfe, kein Automat. Bei einer Kanzlei hängt an jeder fachlichen
 * Aussage eine Haftung.
 */

import { Vorlagen, KI, Status } from '../api.js';
import {
  el,
  ersetzen,
  karte,
  knopf,
  meldung,
  eingabefeld,
  dialog,
  laedt,
  zeitpunkt,
} from '../ui.js';

/**
 * Öffnet den Editor mit einer Vorlage aus der Galerie.
 * @param {any} vorlage
 */
function galerieOeffnen(vorlage) {
  // Über den Sitzungsspeicher, damit die Adresszeile sauber bleibt und ein
  // Neuladen nicht versehentlich eine zweite Vorlage anlegt.
  sessionStorage.setItem('galerie-mjml', vorlage.mjml);
  sessionStorage.setItem('galerie-name', vorlage.name);
  sessionStorage.setItem('galerie-betreff', vorlage.betreff ?? '');
  location.hash = '#/editor';
}

/**
 * Dialog für den KI-Entwurf.
 * @param {() => void} neuLaden
 */
function kiAssistent(neuLaden) {
  const { feld: themaFeld, eingabe: themaEingabe } = eingabefeld({
    beschriftung: 'Worum geht es?',
    platzhalter: 'z. B. Erinnerung an die Arbeitnehmerveranlagung',
    hinweis: 'Je genauer die Angabe, desto brauchbarer der Entwurf.',
  });

  const { feld: zielgruppeFeld, eingabe: zielgruppeEingabe } = eingabefeld({
    beschriftung: 'An wen richtet sich der Newsletter?',
    platzhalter: 'z. B. Einzelunternehmer und kleine GmbHs',
  });

  const ergebnisBereich = el('div');

  const erzeugen = async () => {
    if (!themaEingabe.value.trim()) return;
    ersetzen(ergebnisBereich, laedt('Entwurf wird geschrieben — das dauert etwa eine Minute …'));
    try {
      const entwurf = await KI.entwurf({
        thema: themaEingabe.value.trim(),
        ...(zielgruppeEingabe.value.trim() ? { zielgruppe: zielgruppeEingabe.value.trim() } : {}),
      });

      const uebernehmen = async () => {
        const vorlage = await Vorlagen.anlegen({
          name: `Entwurf: ${themaEingabe.value.trim().slice(0, 40)}`,
          mjml: entwurf.mjml,
          betreff: entwurf.betreff,
        });
        neuLaden();
        location.hash = `#/editor/${vorlage.id}`;
      };

      ersetzen(
        ergebnisBereich,
        meldung(
          'warnung',
          'Der Entwurf ist ein Ausgangspunkt. Alle fachlichen Aussagen, Fristen und Beträge müssen vor dem Versand geprüft werden.',
        ),
        el('div.feld', [
          el('label', { text: 'Vorgeschlagener Betreff' }),
          el('p', { text: entwurf.betreff }),
        ]),
        entwurf.hinweise?.length
          ? el('div.feld', [
              el('label', { text: 'Vor dem Versand zu prüfen' }),
              el(
                'ul',
                entwurf.hinweise.map((/** @type {string} */ h) => el('li', { text: h })),
              ),
            ])
          : null,
        entwurf.blockiert
          ? meldung(
              'fehler',
              'Der Entwurf hat Darstellungsfehler und muss im Editor überarbeitet werden.',
            )
          : null,
        el('div.knopf-reihe', [
          knopf('Als Vorlage übernehmen und öffnen', { art: 'haupt', beiKlick: uebernehmen }),
        ]),
      );
    } catch (fehler) {
      ersetzen(
        ergebnisBereich,
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Entwurf fehlgeschlagen'),
      );
    }
  };

  dialog({
    titel: 'Entwurf vorschlagen lassen',
    inhalt: [
      el('p', {
        text: 'Der Assistent schreibt einen Newsletter in der Gestaltung und Sprache der Kanzlei. Sie überarbeiten ihn anschließend im Editor.',
      }),
      themaFeld,
      zielgruppeFeld,
      ergebnisBereich,
    ],
    aktionen: [knopf('Entwurf erzeugen', { art: 'haupt', beiKlick: erzeugen })],
  });
}

/**
 * Fragt nach und löscht dann.
 *
 * Die Rückfrage ist hier nicht Zierde: eine Vorlage ist Arbeit von einer
 * Stunde, und der Knopf sitzt neben „Bearbeiten".
 *
 * @param {{ id: number, name: string }} vorlage
 * @param {() => Promise<void>} neuLaden
 */
function vorlageLoeschen(vorlage, neuLaden) {
  const meldungsBereich = el('div');

  const schliessen = dialog({
    titel: 'Vorlage löschen',
    inhalt: [
      el('p', { text: `Soll die Vorlage „${vorlage.name}" gelöscht werden?` }),
      el('p.editor-hinweis', {
        text: 'Wird sie von einer Aussendung verwendet, bleibt sie erhalten — sonst wäre nicht mehr nachvollziehbar, was damals versendet wurde.',
      }),
      meldungsBereich,
    ],
    aktionen: [
      knopf('Abbrechen', { beiKlick: () => schliessen() }),
      knopf('Löschen', {
        art: 'haupt',
        beiKlick: async () => {
          ersetzen(meldungsBereich, laedt('Wird gelöscht …'));
          try {
            await Vorlagen.loeschen(vorlage.id);
            schliessen();
            await neuLaden();
          } catch (fehler) {
            ersetzen(
              meldungsBereich,
              meldung('warnung', fehler instanceof Error ? fehler.message : 'Löschen nicht möglich'),
            );
          }
        },
      }),
    ],
  });
}

/**
 * @returns {import('../app.js').Ansicht}
 */
export function vorlagenAnsicht() {
  return {
    titel: 'Vorlagen',
    async zeichnen(behaelter) {
      const eigeneBereich = el('div');

      const laden = async () => {
        ersetzen(eigeneBereich, laedt());
        const vorlagen = await Vorlagen.liste();

        if (!vorlagen.length) {
          ersetzen(
            eigeneBereich,
            el('p.leer', {
              text: 'Noch keine eigene Vorlage. Wählen Sie oben eine fertige Gestaltung — Sie tauschen dann nur noch die Texte aus.',
            }),
          );
          return;
        }

        ersetzen(
          eigeneBereich,
          el(
            'div.auswahl',
            vorlagen.map((vorlage) =>
              el(
                'button.auswahl-eintrag',
                {
                  type: 'button',
                  onclick: () => {
                    location.hash = `#/editor/${vorlage.id}`;
                  },
                },
                [
                  el('span', [
                    el('span.haupttext', { text: vorlage.name }),
                    el('span.nebentext', {
                      text: `${vorlage.betreff || 'Ohne Betreff'} · zuletzt bearbeitet ${zeitpunkt(vorlage.geaendert_am)}`,
                    }),
                  ]),
                  el('span.rechts', [
                    // Kopieren und Löschen sitzen in der Zeile, nicht in einem
                    // Menü: es sind zwei Aktionen, und ein Menü für zwei
                    // Einträge ist ein Klick zu viel.
                    el('button.still', {
                      type: 'button',
                      text: 'Kopie',
                      title: 'Als Kopie anlegen und dort weiterarbeiten',
                      onclick: async (/** @type {Event} */ ereignis) => {
                        // Sonst öffnet der Klick zusätzlich den Editor.
                        ereignis.stopPropagation();
                        const kopie = await Vorlagen.duplizieren(vorlage.id);
                        location.hash = `#/editor/${kopie.id}`;
                      },
                    }),
                    el('button.still', {
                      type: 'button',
                      text: 'Löschen',
                      title: 'Diese Vorlage löschen',
                      onclick: (/** @type {Event} */ ereignis) => {
                        ereignis.stopPropagation();
                        vorlageLoeschen(vorlage, laden);
                      },
                    }),
                    el('span', { text: 'Bearbeiten ›' }),
                  ]),
                ],
              ),
            ),
          ),
        );
      };

      const [status, galerie] = await Promise.all([
        Status.laden().catch(() => null),
        Vorlagen.galerie().catch(() => []),
      ]);

      const galerieRaster = el(
        'div.galerie',
        galerie.map((vorlage) =>
          el(
            'button.vorlagen-karte',
            { type: 'button', onclick: () => galerieOeffnen(vorlage) },
            [
              el('div.vorlagen-bild', [
                el('img', { src: vorlage.vorschaubild, alt: '', loading: 'lazy' }),
              ]),
              el('div.vorlagen-text', [
                el('span.titel', { text: vorlage.name }),
                el('span.beschreibung', { text: vorlage.beschreibung }),
              ]),
            ],
          ),
        ),
      );

      ersetzen(
        behaelter,
        el('div.bahn', [
          el('div.seiten-kopf', [
            el('div', [
              el('h1', { text: 'Vorlagen' }),
              el('p', {
                text: 'Die Gestaltung Ihrer Newsletter. Eine Vorlage lässt sich für beliebig viele Aussendungen verwenden.',
              }),
            ]),
            el('div.knopf-reihe', [
              status?.kiAssistent
                ? knopf('Entwurf vorschlagen lassen', { beiKlick: () => kiAssistent(laden) })
                : null,
              // Der naheliegendste Weg gehört an die naheliegendste Stelle:
              // oben rechts, wo bei den Aussendungen „Neue Aussendung" steht.
              // Vorher führte der einzige Weg zu einer leeren Vorlage über die
              // sechste Karte der Galerie, ganz unten — wer einfach anfangen
              // wollte, fand ihn nicht.
              knopf('Neue Vorlage', {
                art: 'haupt',
                beiKlick: () => {
                  // Ohne Kennung startet der Editor mit dem Grundgerüst:
                  // Logo, Anrede, Fußzeile mit Abmeldelink.
                  sessionStorage.removeItem('galerie-mjml');
                  location.hash = '#/editor';
                },
              }),
            ]),
          ]),

          karte({
            titel: 'Fertige Gestaltungen',
            hinweis:
              'Wählen Sie eine aus — Logo, Farben, Abstände und Abmeldelink sind bereits richtig gesetzt. Sie tauschen nur noch die Texte aus.',
            inhalt: galerie.length
              ? galerieRaster
              : meldung('warnung', 'Die Galerie konnte nicht geladen werden.'),
          }),

          karte({
            titel: 'Ihre Vorlagen',
            inhalt: eigeneBereich,
          }),
        ]),
      );

      await laden();
    },
  };
}
